import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Guard unsaved edits.
 * - Refresh / close tab: native browser confirm (custom UI not allowed)
 * - In-app Link clicks + browser back/forward: call onBlock so host can show modal
 *
 * Call `confirmLeave()` after user confirms discard; `cancelLeave()` if they stay.
 */
export function useUnsavedChangesGuard({ isDirty, onBlock }) {
  const navigate = useNavigate();
  const allowLeaveRef = useRef(false);
  const pendingActionRef = useRef(null);
  const isDirtyRef = useRef(isDirty);
  const onBlockRef = useRef(onBlock);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    onBlockRef.current = onBlock;
  }, [onBlock]);

  const requestLeave = useCallback((action) => {
    pendingActionRef.current = typeof action === 'function' ? action : null;
    onBlockRef.current?.();
  }, []);

  const confirmLeave = useCallback(() => {
    allowLeaveRef.current = true;
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (typeof action === 'function') {
      action();
    }
  }, []);

  const cancelLeave = useCallback(() => {
    pendingActionRef.current = null;
  }, []);

  // Refresh / tab close / hard refresh — browser native dialog only
  useEffect(() => {
    if (!isDirty) return undefined;

    const onBeforeUnload = (e) => {
      if (allowLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // In-app <Link> / <a> navigation
  useEffect(() => {
    if (!isDirty) return undefined;

    const onDocumentClick = (e) => {
      if (allowLeaveRef.current || !isDirtyRef.current) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = e.target.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('javascript:')
      ) {
        return;
      }

      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      const next = `${url.pathname}${url.search}${url.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next === current) return;

      e.preventDefault();
      e.stopPropagation();

      pendingActionRef.current = () => navigate(next);
      onBlockRef.current?.();
    };

    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [isDirty, navigate]);

  // Browser back / forward
  useEffect(() => {
    if (!isDirty) return undefined;

    const marker = { __unsavedGuard: true };
    window.history.pushState(marker, '');

    const onPopState = () => {
      if (allowLeaveRef.current || !isDirtyRef.current) return;

      // Stay on current URL; ask before leaving
      window.history.pushState(marker, '');
      pendingActionRef.current = () => {
        allowLeaveRef.current = true;
        // Skip re-push + original page entry → real previous route
        window.history.go(-2);
      };
      onBlockRef.current?.();
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isDirty]);

  return { requestLeave, confirmLeave, cancelLeave };
}
